import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, FileText, MessageSquare, BarChart3, Eye, EyeOff, Loader2, ArrowLeft, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", res.data.token);
      onLogin(res.data.token);
      toast.success("Login realizado com sucesso!");
    } catch {
      toast.error("Senha incorreta.");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Núcleo" className="h-12 mx-auto mb-4" />
          <h1 className="font-heading font-bold text-navy text-2xl">Painel Administrativo</h1>
          <p className="text-sm text-slate-500 mt-1">Núcleo Corretora de Seguros</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border p-6 shadow-sm space-y-4" data-testid="admin-login-form">
          <Input type="password" placeholder="Senha de acesso" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="admin-password" />
          <Button type="submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-dark text-white" data-testid="admin-login-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
          </Button>
        </form>
        <Link to="/" className="flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-brand mt-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao site
        </Link>
      </div>
    </div>
  );
}

function ArticleEditor({ article, onSave, onClose }) {
  const [form, setForm] = useState(article || { title: "", slug: "", excerpt: "", content: "", category: "geral", image_url: "", published: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` }
      });
      setForm({ ...form, image_url: res.data.url });
      toast.success("Imagem enviada!");
    } catch {
      toast.error("Erro ao enviar imagem.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.content) { toast.error("Título e conteúdo são obrigatórios."); return; }
    setSaving(true);
    try { await onSave(form); onClose(); } catch { toast.error("Erro ao salvar."); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <Input placeholder="Título do artigo *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11" data-testid="article-title" />
      <Input placeholder="Slug (gerado automaticamente)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="h-10 text-sm" data-testid="article-slug" />
      <Input placeholder="Resumo / Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="h-10 text-sm" data-testid="article-excerpt" />
      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
        <SelectTrigger className="h-10" data-testid="article-category"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="geral">Geral</SelectItem>
          <SelectItem value="seguro-auto">Seguro Auto</SelectItem>
          <SelectItem value="seguro-empresarial">Seguro Empresarial</SelectItem>
          <SelectItem value="seguro-vida">Seguro de Vida</SelectItem>
          <SelectItem value="seguro-residencial">Seguro Residencial</SelectItem>
          <SelectItem value="seguro-viagem">Seguro Viagem</SelectItem>
          <SelectItem value="seguro-cyber">Riscos Cibernéticos</SelectItem>
        </SelectContent>
      </Select>

      {/* Image Upload */}
      <div>
        <label className="text-sm text-slate-600 mb-1.5 block">Imagem do Artigo</label>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors" data-testid="article-image-upload">
            <ImageIcon className="h-4 w-4" />
            {uploading ? "Enviando..." : "Escolher Imagem"}
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
          </label>
          {form.image_url && (
            <div className="flex items-center gap-2">
              <img src={form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="Preview" className="h-10 w-16 object-cover rounded border" />
              <button onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-red-500 hover:text-red-700">Remover</button>
            </div>
          )}
        </div>
        <Input placeholder="Ou cole a URL da imagem" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="h-9 text-sm mt-2" data-testid="article-image-url" />
      </div>

      <Textarea placeholder="Conteúdo do artigo *" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="min-h-[200px] text-sm" data-testid="article-content" />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="rounded" data-testid="article-published" />
          Publicar artigo
        </label>
      </div>
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-dark text-white flex-1" data-testid="article-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editArticle, setEditArticle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, blogRes, quotesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/blog?published_only=false`, { headers: authHeaders }),
        axios.get(`${API}/quotes`, { headers: authHeaders }),
        axios.get(`${API}/contacts`, { headers: authHeaders }),
      ]);
      setStats(statsRes.data);
      setArticles(blogRes.data);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("admin_token"); setToken(null); }
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveArticle = async (formData) => {
    const authHeaders = { Authorization: `Bearer ${token}` };
    if (editArticle?.id) {
      await axios.put(`${API}/blog/${editArticle.id}`, formData, { headers: authHeaders });
      toast.success("Artigo atualizado!");
    } else {
      await axios.post(`${API}/blog`, formData, { headers: authHeaders });
      toast.success("Artigo criado!");
    }
    fetchData();
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Deseja excluir este artigo?")) return;
    const authHeaders = { Authorization: `Bearer ${token}` };
    await axios.delete(`${API}/blog/${id}`, { headers: authHeaders });
    toast.success("Artigo excluido!");
    fetchData();
  };

  const handleLogout = () => { localStorage.removeItem("admin_token"); setToken(null); };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Núcleo" className="h-7" />
            <span className="font-heading font-bold text-navy text-sm">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-500 hover:text-brand">Ver site</Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Cotações", value: stats.quotes, icon: BarChart3 },
              { label: "Contatos", value: stats.contacts, icon: MessageSquare },
              { label: "Artigos", value: stats.articles, icon: FileText },
              { label: "Publicados", value: stats.published, icon: Eye },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className="h-4 w-4 text-brand" />
                  <span className="text-xs text-slate-500">{s.label}</span>
                </div>
                <p className="text-2xl font-heading font-bold text-navy">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="blog" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Cotações</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contatos</TabsTrigger>
          </TabsList>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-navy text-lg">Artigos do Blog</h2>
              <Button className="bg-brand hover:bg-brand-dark text-white" onClick={() => { setEditArticle(null); setShowEditor(true); }} data-testid="admin-new-article">
                <Plus className="h-4 w-4 mr-1.5" /> Novo Artigo
              </Button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-white border rounded-lg p-4" data-testid={`admin-article-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-navy text-sm truncate">{a.title}</h3>
                      <Badge variant={a.published ? "default" : "secondary"} className="text-[10px] shrink-0">
                        {a.published ? <><Eye className="h-3 w-3 mr-1" />Publicado</> : <><EyeOff className="h-3 w-3 mr-1" />Rascunho</>}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400">{a.category} | {new Date(a.created_at).toLocaleDateString("pt-BR")}</p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-4">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditArticle(a); setShowEditor(true); }} data-testid={`edit-article-${a.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleDeleteArticle(a.id)} data-testid={`delete-article-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {articles.length === 0 && <p className="text-center text-slate-400 py-8">Nenhum artigo criado ainda.</p>}
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <h2 className="font-heading font-bold text-navy text-lg mb-4">Solicitações de Cotação</h2>
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} className="bg-white border rounded-lg p-4" data-testid={`admin-quote-${q.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-navy text-sm">{q.name}</p>
                      <p className="text-xs text-slate-500">{q.email} | {q.phone}</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">{q.insurance_type}</Badge>
                  </div>
                  {q.message && <p className="text-sm text-slate-600 mt-2">{q.message}</p>}
                  <p className="text-[10px] text-slate-400 mt-2">{new Date(q.created_at).toLocaleString("pt-BR")}</p>
                </div>
              ))}
              {quotes.length === 0 && <p className="text-center text-slate-400 py-8">Nenhuma cotação recebida.</p>}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <h2 className="font-heading font-bold text-navy text-lg mb-4">Mensagens de Contato</h2>
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className="bg-white border rounded-lg p-4" data-testid={`admin-contact-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-navy text-sm">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.email} | {c.phone}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{c.message}</p>
                  <p className="text-[10px] text-slate-400 mt-2">{new Date(c.created_at).toLocaleString("pt-BR")}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-center text-slate-400 py-8">Nenhuma mensagem recebida.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Article Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editArticle ? "Editar Artigo" : "Novo Artigo"}</DialogTitle>
            <DialogDescription>Preencha os campos para {editArticle ? "atualizar" : "criar"} o artigo.</DialogDescription>
          </DialogHeader>
          <ArticleEditor article={editArticle} onSave={handleSaveArticle} onClose={() => setShowEditor(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
